export const OPERATIONS_AREA_COUNTRY_STYLE = Object.freeze({
  fillColor: '#22c55e',
  fillOpacity: 0.3,
  strokeColor: '#15803d',
  strokeOpacity: 0.95,
  strokeWeight: 3,
  clickable: false,
  zIndex: 1,
});

export const DEFAULT_OPERATIONS_AREA_DISPLAY = Object.freeze({
  showCountryHighlight: true,
  highlightIntensity: 30,
  showWatchRadius: true,
});

const normalizedIntensity = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return DEFAULT_OPERATIONS_AREA_DISPLAY.highlightIntensity;
  return Math.min(60, Math.max(10, number));
};

export const operationsAreaCountryStyle = (highlightIntensity) => ({
  ...OPERATIONS_AREA_COUNTRY_STYLE,
  fillOpacity: normalizedIntensity(highlightIntensity) / 100,
});

const asCoordinate = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const countryPolygonCoordinates = (boundary) => {
  const geometry = boundary?.type === 'Feature' ? boundary.geometry : boundary;
  if (geometry?.type === 'Polygon') return [geometry.coordinates || []];
  if (geometry?.type === 'MultiPolygon') return geometry.coordinates || [];
  return [];
};

const googlePolygonPaths = (polygon) => (polygon || [])
  .map((ring) => (ring || [])
    .map((pair) => {
      const longitude = asCoordinate(pair?.[0]);
      const latitude = asCoordinate(pair?.[1]);
      return longitude === null || latitude === null ? null : { lat: latitude, lng: longitude };
    })
    .filter(Boolean))
  .filter((ring) => ring.length >= 3);

/**
 * Install the selected institution's Operations Area on a Google Map.
 * The country uses a clear boundary with a restrained fill so incident and
 * personnel markers remain visually dominant. The watch radius remains a
 * lighter, secondary reference layer.
 */
export const installOperationsAreaOverlay = ({
  mapsApi,
  map,
  area,
  boundary,
  display = DEFAULT_OPERATIONS_AREA_DISPLAY,
}) => {
  if (!mapsApi || !map || !area) return () => {};

  const showCountryHighlight = display?.showCountryHighlight !== false;
  const showWatchRadius = display?.showWatchRadius !== false;
  const countryStyle = operationsAreaCountryStyle(display?.highlightIntensity);

  // Explicit Polygon overlays render consistently above every DisasterMS map
  // theme. Retain the Data layer only as a compatibility fallback.
  const polygons = showCountryHighlight && mapsApi.Polygon
    ? countryPolygonCoordinates(boundary)
      .map(googlePolygonPaths)
      .filter((paths) => paths.length)
      .map((paths) => new mapsApi.Polygon({
        map,
        paths,
        ...countryStyle,
      }))
    : [];
  const dataLayer = showCountryHighlight && boundary && !polygons.length && mapsApi.Data
    ? new mapsApi.Data({ map })
    : null;
  if (dataLayer) {
    dataLayer.setStyle(countryStyle);
    dataLayer.addGeoJson(boundary);
  }

  const latitude = asCoordinate(area.center?.latitude ?? area.center?.lat);
  const longitude = asCoordinate(area.center?.longitude ?? area.center?.lng);
  const radiusKm = asCoordinate(area.radius_km);
  const circle = showWatchRadius && mapsApi.Circle && latitude !== null && longitude !== null
    ? new mapsApi.Circle({
      map,
      center: { lat: latitude, lng: longitude },
      radius: Math.max(50, radiusKm || 750) * 1000,
      fillColor: '#22c55e',
      fillOpacity: 0.025,
      strokeColor: '#16a34a',
      strokeOpacity: 0.55,
      strokeWeight: 1.5,
      clickable: false,
      zIndex: 0,
    })
    : null;

  return () => {
    polygons.forEach((polygon) => polygon.setMap(null));
    dataLayer?.setMap(null);
    circle?.setMap(null);
  };
};
